'use client'

import React from 'react'
import {
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
} from 'lucide-react'

/**
 * LLM Answer Diff visualization for the Origin Story blog post
 * Shows before/after of how AI models described Pixelmojo
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

const providers = [
  {
    name: 'ChatGPT',
    before: {
      status: 'Hallucinated',
      sentiment: 'Cautionary',
      summary:
        'Described Pixelmojo as a "digital marketing agency specializing in social media management and content creation."',
      color: '#F90B8A',
    },
    after: {
      status: 'Cited',
      sentiment: 'Accurate',
      summary:
        'Identifies Pixelmojo as an AI product studio building Vector, Hive, and Radar. References founder Lloyd Pilapil.',
      color: '#22c55e',
    },
  },
  {
    name: 'Perplexity',
    before: {
      status: 'Not cited',
      sentiment: 'Absent',
      summary:
        'No mention of Pixelmojo in any query about AI agencies, product studios, or AI visibility tools.',
      color: '#F90B8A',
    },
    after: {
      status: 'Cited with URL',
      sentiment: 'Positive',
      summary:
        'Cites pixelmojo.io directly. References Radar, free AI visibility tools, and the AI Visibility Stack blog series.',
      color: '#22c55e',
    },
  },
  {
    name: 'Gemini',
    before: {
      status: 'Not cited',
      sentiment: 'Absent',
      summary:
        "Zero results. Pixelmojo did not exist in Gemini's knowledge for any relevant query.",
      color: '#F90B8A',
    },
    after: {
      status: 'Cited',
      sentiment: 'Neutral',
      summary:
        'Mentions Pixelmojo when asked about AI visibility platforms. References structured data and llms.txt approach.',
      color: '#22c55e',
    },
  },
  {
    name: 'Claude',
    before: {
      status: 'Confused',
      sentiment: 'Cautionary',
      summary:
        'Confused Pixelmojo (AI product studio, Philippines) with Pixel Mojo (VR/AR company, Minneapolis) or with a pixel art editor of a similar name.',
      color: '#FDC304',
    },
    after: {
      status: 'Cited',
      sentiment: 'Accurate',
      summary:
        'Correctly distinguishes Pixelmojo from Pixel Mojo. References AI visibility tools, Radar platform, and GEO methodology.',
      color: '#22c55e',
    },
  },
]

function StatusBadge({ status, color }: { status: string; color: string }) {
  const Icon =
    color === '#22c55e'
      ? CheckCircle
      : color === '#FDC304'
        ? AlertTriangle
        : XCircle
  return (
    <span
      className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider'
      style={{ backgroundColor: `${color}15`, color }}
    >
      <Icon className='w-3 h-3' />
      {status}
    </span>
  )
}

export function LLMAnswerDiffDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center gap-2'>
          <TrendingUp className='w-4 h-4 text-muted-foreground' />
          <span className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
            LLM Answer Diff: Pixelmojo
          </span>
        </div>
        <div className='flex items-center gap-4 text-[10px] text-muted-foreground'>
          <span className='font-semibold' style={{ color: '#F90B8A' }}>
            Oct 2025
          </span>
          <ArrowRight className='w-3 h-3' />
          <span className='font-semibold' style={{ color: '#22c55e' }}>
            Mar 2026
          </span>
        </div>
      </div>

      <div className='space-y-3'>
        {providers.map(provider => (
          <div
            key={provider.name}
            className='bg-background border border-border/60 rounded-xl overflow-hidden'
          >
            {/* Provider header */}
            <div className='px-4 py-2.5 border-b border-border/60 bg-muted/30'>
              <span className='text-xs font-bold text-foreground'>
                {provider.name}
              </span>
            </div>

            {/* Before / After grid */}
            <div className='grid grid-cols-2 divide-x divide-border/60'>
              {/* Before */}
              <div className='p-3'>
                <div className='mb-2'>
                  <StatusBadge
                    status={provider.before.status}
                    color={provider.before.color}
                  />
                </div>
                <p className='text-[11px] text-foreground/70 leading-relaxed'>
                  {provider.before.summary}
                </p>
              </div>

              {/* After */}
              <div className='p-3'>
                <div className='mb-2'>
                  <StatusBadge
                    status={provider.after.status}
                    color={provider.after.color}
                  />
                </div>
                <p className='text-[11px] text-foreground/70 leading-relaxed'>
                  {provider.after.summary}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className='mt-5 pt-4 border-t border-border/60 grid grid-cols-3 gap-4 text-center'>
        <div>
          <p className='text-lg font-bold' style={{ color: '#F90B8A' }}>
            0/4
          </p>
          <p className='text-[10px] text-muted-foreground'>Cited (Oct 2025)</p>
        </div>
        <div>
          <p className='text-lg font-bold' style={{ color: '#22c55e' }}>
            4/4
          </p>
          <p className='text-[10px] text-muted-foreground'>Cited (Mar 2026)</p>
        </div>
        <div>
          <p className='text-lg font-bold' style={{ color: '#008dcc' }}>
            516
          </p>
          <p className='text-[10px] text-muted-foreground'>Commits between</p>
        </div>
      </div>
    </div>
  )
}
